import React, { useState, useEffect } from 'react';
import { Activity, AlertCircle, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import api from '../../services/api';

const SMPPDLRMonitoring = () => {
  const [dlrStats, setDlrStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  useEffect(() => {
    fetchDLRStats();
  }, []);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchDLRStats, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const fetchDLRStats = async () => {
    try {
      const response = await api.get('/super/smpp/dlr/monitoring');
      setDlrStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch DLR stats:', error);
      setLoading(false);
    }
  };

  const handleRetryFailed = async () => {
    if (!window.confirm('Retry all failed DLRs from the last hour?')) return;

    try {
      await api.post('/super/smpp/dlr/retry', { max_age_hours: 1 });
      alert('DLR retry initiated');
      fetchDLRStats();
    } catch (error) {
      alert('Failed to retry DLRs: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
      case 'rejected':
      case 'undelivered':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <Activity className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">DLR Monitoring</h1>
          <p className="text-gray-600 mt-2">Track delivery receipt pipeline and status distribution</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-700">Auto-refresh</span>
          </label>
          <button
            onClick={() => window.location.href = '/super/smpp'}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            ← Back
          </button>
        </div>
      </div>

      {dlrStats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Incoming Rate</h3>
                <Activity className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{dlrStats.incoming_rate}</p>
              <p className="text-xs text-gray-500 mt-1">DLRs per second</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Failed DLRs</h3>
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{dlrStats.failed_dlrs}</p>
              <p className="text-xs text-gray-500 mt-1">Last hour</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Pending DLRs</h3>
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{dlrStats.pending_dlrs}</p>
              <p className="text-xs text-gray-500 mt-1">Awaiting processing</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Last Updated</h3>
                <RefreshCw className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-lg font-bold text-gray-900">
                {new Date(dlrStats.last_updated).toLocaleTimeString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">Real-time data</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Status Distribution</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {Object.entries(dlrStats.status_distribution || {}).map(([status, count]) => {
                    const total = Object.values(dlrStats.status_distribution).reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                    
                    return (
                      <div key={status} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(status)}
                            <span className="font-medium text-gray-900 capitalize">{status}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">{count}</p>
                            <p className="text-xs text-gray-500">{percentage}%</p>
                          </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${
                              status.toLowerCase() === 'delivered' ? 'bg-green-500' :
                              status.toLowerCase() === 'pending' ? 'bg-yellow-500' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {Object.keys(dlrStats.status_distribution || {}).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No DLR data available</p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Operator Latency</h3>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {Object.entries(dlrStats.operator_latency || {}).map(([operator, latency]) => (
                    <div key={operator} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{operator}</span>
                        <span className="text-lg font-bold text-blue-600">
                          {latency.toFixed(2)}s
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full ${
                            latency < 2 ? 'bg-green-500' :
                            latency < 5 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${Math.min((latency / 10) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                {Object.keys(dlrStats.operator_latency || {}).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No latency data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <button
              onClick={handleRetryFailed}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Retry Failed DLRs
            </button>
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-semibold text-blue-900 mb-1">
                  DLR Pipeline Information
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• DLRs are processed in real-time via Kafka consumer</li>
                  <li>• Status distribution shows last 1 hour of activity</li>
                  <li>• Operator latency indicates average time from submit to DLR receipt</li>
                  <li>• Failed DLRs can be retried manually or will auto-retry based on configuration</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SMPPDLRMonitoring;
